import { Avatar, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useResolveAsset } from '../context';

const AVATAR = 72; // px — circular photo "dot", echoes the main-page TimelineDot

export interface DayTimelineEventProps {
  /** Free-form range, e.g. "4:30 – 6:00 PM". */
  time: string;
  title: string;
  /** Blog-relative image path, e.g. "images/diamond-head.jpg". */
  image?: string;
  children?: React.ReactNode;
}

/**
 * Config-only child: `DayTimeline` reads these props. It renders nothing on
 * its own (compound-component pattern), so layout stays owned by the parent.
 */
export const DayTimelineEvent: React.FC<DayTimelineEventProps> = () => null;

// --- shared bits -----------------------------------------------------------

// `as typeof Typography` preserves OverridableComponent typing so
// `component="div"` stays type-checked (per docs/styling/mui-styling.md).
const Time = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'none',
})) as typeof Typography;

const Photo: React.FC<{ image?: string; title: string }> = ({ image, title }) => {
  const resolveAsset = useResolveAsset();
  return (
    <Avatar
      src={image ? resolveAsset(image) : undefined}
      alt={title}
      sx={{
        width: AVATAR,
        height: AVATAR,
        border: 3,
        borderColor: 'primary.main',
        boxShadow: 3,
        bgcolor: 'primary.dark',
        zIndex: 1, // sit above the connector line
      }}
    />
  );
};

// --- horizontal (md/lg/xl): snake-wrap -------------------------------------

const RowGrid = styled(Box, {
  shouldForwardProp: (p) => p !== '$cols',
})<{ $cols: number }>(({ theme, $cols }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${$cols}, 1fr)`,
  columnGap: theme.spacing(2),
  alignItems: 'start',
}));

// Half-lines on the avatar band: each cell draws center→left and center→right,
// so adjacent cells' halves meet and form one continuous line through the
// avatar centers. Row-end sides are suppressed via $first/$last.
const AvatarBand = styled(Box, {
  shouldForwardProp: (p) => p !== '$first' && p !== '$last',
})<{ $first: boolean; $last: boolean }>(({ theme, $first, $last }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: AVATAR,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    height: 3,
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.primary.main,
    zIndex: 0,
  },
  '&::before': { left: 0, right: '50%', display: $first ? 'none' : 'block' },
  '&::after': { left: '50%', right: 0, display: $last ? 'none' : 'block' },
}));

// Down-elbow between rows, x-positioned under the avatar where the row ended.
const RowDown = styled(Box, {
  shouldForwardProp: (p) => p !== '$pct',
})<{ $pct: number }>(({ theme, $pct }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: 24,
  marginLeft: `calc(${$pct}% - 12px)`,
  color: theme.palette.primary.main,
  '& .line': {
    width: 3,
    height: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
  },
}));

const Node: React.FC<{ ev: DayTimelineEventProps; first: boolean; last: boolean }> = ({
  ev,
  first,
  last,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
    <Time variant="caption">{ev.time}</Time>
    <AvatarBand $first={first} $last={last}>
      <Photo image={ev.image} title={ev.title} />
    </AvatarBand>
    <Box sx={{ textAlign: 'center', px: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {ev.title}
      </Typography>
      {ev.children && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {ev.children}
        </Typography>
      )}
    </Box>
  </Box>
);

const Horizontal: React.FC<{ events: DayTimelineEventProps[]; cols: number }> = ({
  events,
  cols,
}) => {
  const rows: DayTimelineEventProps[][] = [];
  for (let i = 0; i < events.length; i += cols) rows.push(events.slice(i, i + cols));

  return (
    <Box sx={{ my: 4 }}>
      {rows.map((row, r) => {
        // Odd rows flow right→left (snake): reverse the cells visually.
        const reversed = r % 2 === 1;
        const cells = reversed ? [...row].reverse() : row;
        const notLastRow = r < rows.length - 1;
        // Chronologically-last event of this row sits on the end side:
        // right for L→R rows, left for reversed rows.
        const endPct = reversed
          ? (0.5 / cols) * 100
          : ((row.length - 0.5) / cols) * 100;

        return (
          <React.Fragment key={r}>
            <RowGrid $cols={cols}>
              {cells.map((ev, i) => (
                <Node
                  key={i}
                  ev={ev}
                  first={i === 0}
                  last={i === cells.length - 1}
                />
              ))}
            </RowGrid>
            {notLastRow && (
              <RowDown $pct={endPct}>
                <span className="line" />
                <KeyboardArrowDown />
              </RowDown>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// --- vertical (sm/xs) ------------------------------------------------------

const Vertical: React.FC<{ events: DayTimelineEventProps[] }> = ({ events }) => (
  <Box sx={{ my: 4 }}>
    {events.map((ev, i) => (
      <Box
        key={i}
        sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Photo image={ev.image} title={ev.title} />
          {i < events.length - 1 && (
            <Box sx={{ flex: 1, width: '3px', bgcolor: 'primary.main', my: 1 }} />
          )}
        </Box>
        <Box sx={{ pb: 4 }}>
          <Time variant="caption" component="div">
            {ev.time}
          </Time>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {ev.title}
          </Typography>
          {ev.children && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {ev.children}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

// --- public component ------------------------------------------------------

/**
 * Horizontal, snake-wrapping day timeline for trip blogs. Children are
 * `<DayTimelineEvent>` elements; their props are read here.
 *
 *   <DayTimeline date="Day 1 — Honolulu">
 *     <DayTimelineEvent time="8–9 AM" title="Sunrise" image="images/a.jpg">
 *       Hiked Diamond Head before the crowds.
 *     </DayTimelineEvent>
 *   </DayTimeline>
 */
const DayTimeline: React.FC<{ date?: string; children?: React.ReactNode }> = ({
  date,
  children,
}) => {
  const theme = useTheme();
  const xl = useMediaQuery(theme.breakpoints.up('xl'));
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const cols = xl ? 5 : lg ? 4 : md ? 3 : 0; // 0 → vertical (sm/xs)

  const events = React.Children.toArray(children)
    .filter((c): c is React.ReactElement<DayTimelineEventProps> => React.isValidElement(c))
    .map((c) => c.props);

  if (events.length === 0) return null;

  return (
    <Box sx={{ my: 5 }}>
      {date && (
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {date}
        </Typography>
      )}
      {cols === 0 ? (
        <Vertical events={events} />
      ) : (
        <Horizontal events={events} cols={cols} />
      )}
    </Box>
  );
};

export default DayTimeline;
